/**
 * features/dashboard/useHeadInsights.ts
 * ------------------------------------------------------------------
 * The Principal dashboard's "At a glance" data layer. DataContext does
 * NOT stream marks / comments / flags / discipline / attendance, so
 * this hook fetches them once (on mount) and derives the headline
 * numbers the Head reaches for first:
 *
 *   • Mark entry    — CA %, Exam % (reuse lib/marksCompliance) and the
 *                     class-teacher comment %.
 *   • Pupils at risk — school-wide, via lib/atRisk (the SAME Al-Nur
 *                     rule the Student Analysis screen uses).
 *   • Top classes / Needs attention — per-class + per-subject averages,
 *                     derived with the SAME deriveMark the report card
 *                     and marksheet use (exam basis in exam terms).
 *   • Open items    — unresolved comment flags + open discipline cases.
 *   • Attendance today — only when a register has actually been marked
 *                     today (else the card is hidden by the caller).
 *
 * Everything is computed off the shared, non-archived roster
 * (activeStudents / non-archived classes) so alumni never skew a figure.
 * Read-only: this hook never writes.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useData, COLLECTIONS } from '@/contexts/DataContext';
import {
  deriveMark,
  schemeForSubject,
  getFormLevel,
  getPassMark,
  termUsesExam,
} from '@/lib/grading';
import { isJuneCohort } from '@/lib/cohort';
import { classifyAtRisk, type RiskTier } from '@/lib/atRisk';
import { buildCaCompliance, buildExamCompliance } from '@/lib/marksCompliance';
import { getCaLabels } from '@/lib/assessmentConfig';
import type {
  MarksDocument,
  SubjectMarkEntry,
  ReportCommentsDocument,
  DailyAttendanceDoc,
} from '@/types';

export interface HeadInsights {
  loading: boolean;
  markEntry: {
    ca: number | null;
    exam: number | null;
    comments: number | null;
    overall: number | null;
  };
  atRisk: { total: number; critical: number; borderline: number };
  topClasses: { classId: string; name: string; average: number; passMark: number }[];
  needsAttention: {
    key: string;
    label: string;
    sub: string;
    value: string;
    severity: 'crit' | 'warn';
  }[];
  openItems: { flags: number; discipline: number; criticalThisMonth: number };
  attendanceToday: {
    present: number;
    absent: number;
    late: number;
    classesMarked: number;
    totalClasses: number;
    pct: number;
  } | null;
  /** Full drill-down lists behind the headline numbers, for the Head
   *  insight-detail views (each stat opens its exact list). */
  lists: {
    atRisk: {
      studentId: string;
      fullName: string;
      className: string;
      tier: RiskTier;
      average: number | null;
      failed: string[];
      borderline: string[];
    }[];
    classesRanked: { classId: string; name: string; average: number; passMark: number }[];
    subjectLows: { key: string; className: string; subjectName: string; avg: number; passMark: number }[];
    absentToday: { studentId: string; name: string; className: string; status: string }[];
  };
}

const round1 = (n: number) => Math.round(n * 10) / 10;
const mean = (xs: number[]) =>
  xs.length ? round1(xs.reduce((a, b) => a + b, 0) / xs.length) : 0;
const pct = (part: number, whole: number) =>
  whole > 0 ? Math.round((part / whole) * 100) : null;
const todayISO = () => new Date().toISOString().slice(0, 10);

export function useHeadInsights(year: string, term: string): HeadInsights {
  const {
    activeStudents,
    classes,
    subjects,
    teachers,
    assessmentConfigs,
    studentById,
  } = useData();

  const [markDocs, setMarkDocs] = useState<MarksDocument[]>([]);
  const [commentDocs, setCommentDocs] = useState<ReportCommentsDocument[]>([]);
  const [flagDocs, setFlagDocs] = useState<{ status?: string }[]>([]);
  const [disciplineDocs, setDisciplineDocs] = useState<
    { status?: string; severity?: string; incidentDate?: string }[]
  >([]);
  const [attToday, setAttToday] = useState<DailyAttendanceDoc[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const today = todayISO();
        const [mSnap, cSnap, fSnap, dSnap, aSnap] = await Promise.all([
          getDocs(collection(db, COLLECTIONS.marks)),
          getDocs(collection(db, COLLECTIONS.reportComments)),
          getDocs(collection(db, COLLECTIONS.flags)),
          getDocs(collection(db, COLLECTIONS.discipline)),
          getDocs(
            query(collection(db, COLLECTIONS.dailyAttendance), where('date', '==', today)),
          ),
        ]);
        if (cancelled) return;
        setMarkDocs(mSnap.docs.map((d) => d.data() as MarksDocument));
        setCommentDocs(cSnap.docs.map((d) => d.data() as ReportCommentsDocument));
        setFlagDocs(fSnap.docs.map((d) => d.data() as { status?: string }));
        setDisciplineDocs(
          dSnap.docs.map((d) => d.data() as { status?: string; severity?: string; incidentDate?: string }),
        );
        setAttToday(aSnap.docs.map((d) => d.data() as DailyAttendanceDoc));
      } catch (e) {
        console.error('Head insights load failed:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // class-subject -> { studentId: entry } for the selected term
  const entriesByCS = useMemo(() => {
    const m = new Map<string, Record<string, SubjectMarkEntry>>();
    for (const d of markDocs) {
      if (d.year === year && d.term === term && d.classId && d.subjectId) {
        m.set(`${d.classId}|${d.subjectId}`, d.entries ?? {});
      }
    }
    return m;
  }, [markDocs, year, term]);

  const activeClasses = useMemo(() => classes.filter((c) => !c.archived), [classes]);
  const examClasses = useMemo(
    () => activeClasses.filter((c) => !isJuneCohort(c)),
    [activeClasses],
  );

  // ---- CA / Exam mark-entry compliance (same lib the screen uses) ----
  const { ca, exam, examIncomplete } = useMemo(() => {
    const getEntries = (cid: string, sid: string) => entriesByCS.get(`${cid}|${sid}`);
    const caLabel = getCaLabels(term, assessmentConfigs)[0];
    const caRows = caLabel
      ? buildCaCompliance({ caLabel, students: activeStudents, classes: activeClasses, subjects, teachers, getEntries })
      : [];
    const examRows = buildExamCompliance({ students: activeStudents, classes: examClasses, subjects, teachers, getEntries });
    return {
      ca: pct(caRows.filter((r) => r.status === 'complete').length, caRows.length),
      exam: pct(examRows.filter((r) => r.status === 'complete').length, examRows.length),
      examIncomplete: examRows.filter((r) => r.status !== 'complete').length,
    };
  }, [entriesByCS, term, assessmentConfigs, activeStudents, activeClasses, examClasses, subjects, teachers]);

  // ---- class-teacher comment % (share of the active roster with one) ----
  const comments = useMemo(() => {
    const activeIds = new Set(activeStudents.map((s) => s.id));
    const commented = new Set<string>();
    for (const d of commentDocs) {
      if (d.year !== year || d.term !== term) continue;
      for (const [sid, c] of Object.entries(d.entries ?? {})) {
        if (activeIds.has(sid) && (c?.classTeacherComment ?? '').trim()) commented.add(sid);
      }
    }
    return pct(commented.size, activeStudents.length);
  }, [commentDocs, year, term, activeStudents]);

  // ---- per-class derivation: at-risk, class avg, subject avgs ----
  const { atRisk, topClasses, subjectLows, atRiskList, classesRanked } = useMemo(() => {
    const useExamBasis = termUsesExam(term);
    let critical = 0;
    let borderline = 0;
    const classAvgs: { classId: string; name: string; average: number; passMark: number }[] = [];
    const subjectLows: { key: string; className: string; subjectName: string; avg: number; passMark: number }[] = [];
    const atRiskList: HeadInsights['lists']['atRisk'] = [];

    for (const cls of activeClasses) {
      const roster = activeStudents.filter((s) => s.currentClass === cls.id);
      if (roster.length === 0) continue;
      const passMark = getPassMark(getFormLevel(cls.name));
      const jc = isJuneCohort(cls);
      const subjIds = new Set<string>();
      roster.forEach((s) => s.subjects?.forEach((id) => subjIds.add(id)));
      const clsSubjects = subjects.filter((s) => subjIds.has(s.id));

      const matrix: Record<string, Record<string, number | null>> = {};
      const subjectTotals = new Map<string, { sum: number; n: number }>();
      const studentAverages: number[] = [];

      for (const s of roster) {
        const row: Record<string, number | null> = {};
        const nums: number[] = [];
        for (const sub of clsSubjects) {
          const entry = entriesByCS.get(`${cls.id}|${sub.id}`)?.[s.id];
          let value: number | null = null;
          if (entry) {
            const scheme = schemeForSubject(sub);
            const d = deriveMark(jc ? 'Term1' : term, entry.ca, jc ? undefined : entry.exam, scheme);
            const useExam = useExamBasis && !jc;
            const exNum = d.exam === '' || d.exam == null || d.exam === 'ABS' ? null : Number(d.exam);
            value = useExam ? exNum : d.caMark;
          }
          row[sub.id] = value;
          if (value !== null) {
            nums.push(value);
            const t = subjectTotals.get(sub.id) ?? { sum: 0, n: 0 };
            t.sum += value;
            t.n += 1;
            subjectTotals.set(sub.id, t);
          }
        }
        matrix[s.id] = row;
        if (nums.length) studentAverages.push(mean(nums));
      }

      const risk = classifyAtRisk({ students: roster, subjects: clsSubjects, passMark, matrix });
      for (const r of risk) {
        r.tier === 'critical' ? (critical += 1) : (borderline += 1);
        atRiskList.push({
          studentId: r.studentId,
          fullName: r.fullName,
          className: cls.name,
          tier: r.tier,
          average: r.average,
          failed: r.failedSubjects.map((f) => f.subjectName),
          borderline: r.borderlineSubjects.map((b) => b.subjectName),
        });
      }

      if (studentAverages.length) {
        classAvgs.push({ classId: cls.id, name: cls.name, average: mean(studentAverages), passMark });
      }
      for (const [sid, t] of subjectTotals) {
        if (t.n < 5) continue; // ignore tiny samples
        const avg = t.sum / t.n;
        if (avg < passMark) {
          subjectLows.push({
            key: `${cls.id}|${sid}`,
            className: cls.name,
            subjectName: subjects.find((x) => x.id === sid)?.name ?? sid,
            avg,
            passMark,
          });
        }
      }
    }

    const classesRanked = classAvgs.slice().sort((a, b) => b.average - a.average);
    const topClasses = classesRanked.slice(0, 5);
    subjectLows.sort((a, b) => a.avg - b.avg);
    // At-risk ordering: critical first, then lowest average.
    atRiskList.sort(
      (a, b) =>
        (a.tier === b.tier ? 0 : a.tier === 'critical' ? -1 : 1) ||
        (a.average ?? 100) - (b.average ?? 100),
    );
    return {
      atRisk: { total: critical + borderline, critical, borderline },
      topClasses,
      subjectLows,
      atRiskList,
      classesRanked,
    };
  }, [activeClasses, activeStudents, subjects, entriesByCS, term]);

  const needsAttention = useMemo(() => {
    const out: HeadInsights['needsAttention'] = subjectLows.slice(0, 4).map((s) => ({
      key: s.key,
      label: `${s.className} · ${s.subjectName}`,
      sub: 'class average below pass mark',
      value: `${Math.round(s.avg)}%`,
      severity: 'crit' as const,
    }));
    if (examIncomplete > 0) {
      out.push({
        key: 'exam-outstanding',
        label: 'Exam marks outstanding',
        sub: `${examIncomplete} class-group${examIncomplete === 1 ? '' : 's'} still to enter`,
        value: 'exam',
        severity: 'warn',
      });
    }
    return out;
  }, [subjectLows, examIncomplete]);

  const openItems = useMemo(() => {
    const monthPrefix = todayISO().slice(0, 7);
    return {
      flags: flagDocs.filter((f) => f.status && f.status !== 'resolved').length,
      discipline: disciplineDocs.filter((d) => d.status === 'open').length,
      criticalThisMonth: disciplineDocs.filter(
        (d) => d.severity === 'critical' && (d.incidentDate ?? '').startsWith(monthPrefix),
      ).length,
    };
  }, [flagDocs, disciplineDocs]);

  const attendanceToday = useMemo(() => {
    const activeIds = new Set(activeClasses.map((c) => c.id));
    const docs = attToday.filter((d) => d.classId && activeIds.has(d.classId));
    if (docs.length === 0) return null;
    let present = 0;
    let absent = 0;
    let late = 0;
    let marked = 0;
    const classSet = new Set<string>();
    for (const d of docs) {
      classSet.add(d.classId);
      for (const st of Object.values(d.entries ?? {})) {
        marked += 1;
        if (st === 'present') present += 1;
        // SICK is regarded as absent (policy Aug 2026).
        else if (st === 'absent' || st === 'sick') absent += 1;
        else if (st === 'late') late += 1;
      }
    }
    return {
      present,
      absent,
      late,
      classesMarked: classSet.size,
      totalClasses: activeClasses.length,
      pct: marked > 0 ? Math.round((present / marked) * 100) : 0,
    };
  }, [attToday, activeClasses]);

  const absentToday = useMemo(() => {
    const activeIds = new Set(activeClasses.map((c) => c.id));
    const out: HeadInsights['lists']['absentToday'] = [];
    for (const d of attToday) {
      if (!d.classId || !activeIds.has(d.classId)) continue;
      for (const [sid, st] of Object.entries(d.entries ?? {})) {
        // SICK regarded as absent (policy Aug 2026) — list it alongside absent/late.
        if (st === 'absent' || st === 'late' || st === 'sick') {
          out.push({
            studentId: sid,
            name: studentById(sid)?.fullName ?? sid,
            className: d.className,
            status: st,
          });
        }
      }
    }
    return out.sort(
      (a, b) => a.className.localeCompare(b.className) || a.name.localeCompare(b.name),
    );
  }, [attToday, activeClasses, studentById]);

  const markEntry = useMemo(() => {
    const vals = [ca, exam, comments].filter((v): v is number => v != null);
    return {
      ca,
      exam,
      comments,
      overall: vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : null,
    };
  }, [ca, exam, comments]);

  return {
    loading,
    markEntry,
    atRisk,
    topClasses,
    needsAttention,
    openItems,
    attendanceToday,
    lists: { atRisk: atRiskList, classesRanked, subjectLows, absentToday },
  };
}
